import {SagaIterator} from 'redux-saga';
import {all, call, cancel, put, select, takeEvery} from 'redux-saga/effects';

import {AMAZON_BUTTON_ID_V2, AMAZON_URL_PARAM} from 'desktop/pages/checkout/constants/amazon';

import {STEPS} from 'desktop/pages/checkout/constants/checkout';
import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';
import {
    eventAmazonAuth,
    eventAmazonReady,
    fetchAmazonSessionInfo,
    fetchAmazonSessionInfoToSaga,
    methodAmazonAccessToken,
    methodAmazonAddressSelected,
    methodAmazonAuth,
    methodAmazonPaymentConfirmed,
    methodAmazonPaymentReady,
    saveShippingAddressOnAmazonPayment,
} from 'desktop/pages/checkout/store/reducers/paymentStep/amazonV2/amazonV2';
import {setSelectedMethod as setSelectedMethodSideReview} from 'desktop/pages/checkout/store/reducers/paymentStep/selectedMethod/selectedMethod';
import {startSetCheckoutStepSaga as startSetCheckoutStepSagaSideReview} from 'desktop/pages/checkout/store/reducers/sagaStep/sagaStep';
import {changeStepStatus as changeStepStatusSideReview} from 'desktop/pages/checkout/store/reducers/status/status';
import {getOrderReviewTotals as getOrderReviewTotalsSelector} from 'desktop/pages/checkout/store/selectors/orderReviewTotals/orderReviewTotals';
import {
    getAmazonV2,
    getNameSelectedMethod,
    getSelectedMethod,
} from 'desktop/pages/checkout/store/selectors/paymentStep/paymentStep';

import {getAllStates} from 'desktop/selectors/location/location';
import {amazonButtonV2} from 'libs/amazon/button/buttonV2';

import {apiConfig} from 'libs/api/api';
import {getUrlParams} from 'libs/url/url';

import {getShippingMethodsSaga} from '../../../../deliveryStep/getShippingMethodsSaga/getShippingMethodsSaga';
import {saveCustomerAddressSagaFromAmazon} from '../../../../deliveryStep/saveCustomerAddressSaga/saveCustomerAddressSaga';
import {fetchAndSetAmazonSessionInfo} from '../../../payerData/payerData';
import {scrollToInPaymentStep} from '../../../utils/scrollToInPaymentStep';
import {getBillingAddressForSave, getShippingAddressForSave} from '../utils/getAddressesForSave';
import {getCurrentRegions} from '../utils/getCurrentRegion';
import {sendAnalyticsAmazonAuthButton} from '../utils/sendAnalyticsButtonAuth';

/**
 * Return amazon button selector
 */
export function* getAmazonButtonSelector(): SagaIterator {
    return yield call([document, 'querySelector'], `#${AMAZON_BUTTON_ID_V2}`);
}

/**
 * Show button for auth on amazon
 */
export function* showButtonAuthAmazon(): SagaIterator {
    const selectedMethod = yield select(getSelectedMethod);
    const {grandTotal} = yield select(getOrderReviewTotalsSelector);
    const buttonCount = yield call(getAmazonButtonSelector);

    if (buttonCount && buttonCount.getAttribute('title')) yield cancel();

    yield call(amazonButtonV2, {
        color: selectedMethod.button.color,
        handleSendAnalytics: sendAnalyticsAmazonAuthButton,
        isSandbox: Boolean(Number(selectedMethod.button.isSandbox)),
        sellerId: selectedMethod.button.sellerId,
        total: grandTotal,
    });
}

/**
 * Show button auth
 */
export function* initialAmazon(): SagaIterator {
    const name = yield select(getNameSelectedMethod);
    const selectedMethod = yield select(getSelectedMethod);
    const amazon = yield select(getAmazonV2);

    if (name !== PAYMENTS.METHOD_AMAZON_V2 || !selectedMethod) yield cancel();

    if (window.amazon && !amazon.auth) yield call(showButtonAuthAmazon);

    yield call(scrollToInPaymentStep, PAYMENTS.METHOD_AMAZON_V2);
}

/**
 * Actions after authorization Amazon for side review
 */
export function* onAmazonAuthActionsSaga(): SagaIterator {
    yield put(setSelectedMethodSideReview(PAYMENTS.METHOD_AMAZON_V2));
    yield put(
        changeStepStatusSideReview({
            [STEPS.DELIVERY_STEP]: {
                completed: true,
                loaded: true,
            },
            [STEPS.PAYMENT_STEP]: {
                completed: false,
            },
        }),
    );
    yield put(startSetCheckoutStepSagaSideReview(STEPS.PAYMENT_STEP));
}

/**
 * Auth on amazon
 */
export function* amazonAuth(): SagaIterator {
    const {search: searchParam} = apiConfig.hostStore.getState().router.location;
    const urlParams = yield call(getUrlParams, searchParam);
    const paymentConfirmed = urlParams[AMAZON_URL_PARAM.PAYMENT_CONFIRMED];
    const token = urlParams[AMAZON_URL_PARAM.SESSION_ID];

    if (typeof paymentConfirmed === 'undefined' || typeof token === 'undefined') yield cancel();

    yield all([
        put(methodAmazonAuth(true)),
        put(methodAmazonAccessToken(token[0])),
        put(methodAmazonPaymentConfirmed(paymentConfirmed[0] === 'true')),
        call(onAmazonAuthActionsSaga),
    ]);
}

/**
 * Save amazon address to quote on backend
 */
export function* amazonAddressSaveSaga(): SagaIterator {
    const amazon = yield select(getAmazonV2);
    const regions = yield select(getAllStates);

    if (!amazon.sessionInfo.data) yield cancel();

    const {billingAddress, buyer, shippingAddress} = amazon.sessionInfo.data;

    const {billingRegion, shippingRegion} = yield call(
        getCurrentRegions,
        regions,
        shippingAddress,
        billingAddress,
    );

    yield call(
        saveCustomerAddressSagaFromAmazon,
        getShippingAddressForSave(shippingAddress, shippingRegion, buyer),
        getBillingAddressForSave(billingAddress, billingRegion, buyer),
    );
    yield call(getShippingMethodsSaga, {
        country: shippingAddress.country,
        postalCode: shippingAddress.postalCode,
    });
}

/**
 * Set true on address ready
 */
export function* amazonAddressSelect(): SagaIterator {
    const amazon = yield select(getAmazonV2);

    yield put(methodAmazonAddressSelected(true));
    const {shippingAddress} = amazon.sessionInfo.data;

    if (amazon.paymentConfirmed) {
        yield call(getShippingMethodsSaga, {
            country: shippingAddress.country,
            postalCode: shippingAddress.postalCode,
        });
    } else {
        yield call(amazonAddressSaveSaga);
    }

    yield put(saveShippingAddressOnAmazonPayment());
}

/**
 * Set true on payment ready
 */
export function* amazonPaymentReady(): SagaIterator {
    const amazon = yield select(getAmazonV2);

    yield put(methodAmazonPaymentReady(amazon.addressSelect));
}

/**
 *  Call save addresses and payment ready after complete fetch amazon session info
 */
export function* fetchAmazonSessionInfoComplete(): SagaIterator {
    yield call(amazonAddressSelect);
    yield call(amazonPaymentReady);
}

/**
 * Begin of amazon saga
 */
export function* amazonSaga(): SagaIterator {
    yield takeEvery(`${eventAmazonReady}`, initialAmazon);
    yield takeEvery(`${eventAmazonAuth}`, amazonAuth);
    yield takeEvery(`${fetchAmazonSessionInfoToSaga}`, fetchAndSetAmazonSessionInfo);
    yield takeEvery(`${fetchAmazonSessionInfo.fulfilled}`, fetchAmazonSessionInfoComplete);
}
